/**
 * Production-path gateway: a thin HTTP client over the BFF's org-scoped API.
 * All data lives behind the BFF (file-backed today, database later) — this
 * class holds no data of its own.
 */
import type { Agent, OrgId, ProcessDoc, ProcessPatch, ProcessSummary } from '@wand/shared';
import { api } from '../api/client.js';
import type { Group } from '../data/groups.js';
import type { SignalMetric } from '../data/metrics.js';
import type { InboxItem } from '../data/requests.js';
import type { WorkforceGateway } from './types.js';
import type { OrgDataset } from './dataset.js';
import type { WizardEvent, WizardInput, WizardStream } from './wizard.js';

export class ApiGateway implements WorkforceGateway {
  getDataset(org: OrgId): Promise<OrgDataset> {
    return api.getDataset(org);
  }

  saveAgent(org: OrgId, pk: string, patch: Partial<Agent>): Promise<Agent> {
    return api.patchAgent(org, pk, patch);
  }

  createAgent(org: OrgId, input: Omit<Agent, 'pk'>): Promise<Agent> {
    return api.createAgent(org, input);
  }

  createGroup(org: OrgId, input: Omit<Group, 'pk'>): Promise<Group> {
    return api.createGroup(org, input);
  }

  updateGroup(org: OrgId, pk: string, patch: Partial<Omit<Group, 'pk'>>): Promise<Group> {
    return api.patchGroup(org, pk, patch);
  }

  createMetric(org: OrgId, input: Omit<SignalMetric, 'id'> & { id?: string }): Promise<SignalMetric> {
    return api.createMetric(org, input);
  }

  resolveInboxItem(org: OrgId, id: string, actionId: string): Promise<InboxItem> {
    return api.resolveInboxItem(org, id, actionId);
  }

  replyInboxItem(org: OrgId, id: string, text: string): Promise<InboxItem> {
    return api.replyInboxItem(org, id, text);
  }

  askInboxItem(org: OrgId, id: string, text: string): Promise<InboxItem> {
    return api.askInboxItem(org, id, text);
  }

  converse(_org: OrgId, input: WizardInput): WizardStream {
    // Wraps the existing request/reply BFF endpoint as a one-message stream. A
    // real backend replaces this generator with an SSE reader.
    async function* gen(): AsyncGenerator<WizardEvent> {
      try {
        const { reply } = await api.wizard(input.text, input.processId);
        yield { type: 'message', text: reply };
      } catch {
        yield { type: 'message', text: 'Sorry — I could not reach the Wizard service just now.' };
      }
      yield { type: 'done' };
    }
    return { events: gen(), answer() {}, cancel() {} };
  }

  // ---- editable process documents (the canvas) ----

  listProcessDocs(org: OrgId): Promise<ProcessSummary[]> {
    return api.listProcesses(org);
  }
  getProcessDoc(org: OrgId, id: string): Promise<ProcessDoc | undefined> {
    return api.getProcess(org, id);
  }
  createProcessDoc(org: OrgId, name?: string): Promise<ProcessDoc> {
    return api.createProcess(org, name);
  }
  saveProcessDoc(org: OrgId, id: string, patch: ProcessPatch): Promise<ProcessDoc> {
    return api.saveProcess(org, id, patch);
  }
}
